import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Users, Calendar, CheckCircle, XCircle, Clock, ArrowLeft, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import { getUser, getToken } from "../utils/authService";


// Extracted components for better maintainability
import { formatFileSize } from "../components/hr";


import DocumentDialogSection from "../components/hr/sections/DocumentDialogSection";
import PhotoDialogSection from "../components/hr/sections/PhotoDialogSection";
import ArchiveDialogSection from "../components/hr/sections/ArchiveDialogSection";
import LeaveApprovalDialogSection from "../components/hr/sections/LeaveApprovalDialogSection";
import EmployeesSection from "../components/hr/sections/EmployeesSection";
import LeavesSection from "../components/hr/sections/LeavesSection";

const HRManagement = () => {
  const [activeTab, setActiveTab] = useState("employees");
  const [employees, setEmployees] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  
  // Archive dialog state
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [employeeToArchive, setEmployeeToArchive] = useState(null);
  const [archiveReason, setArchiveReason] = useState("");
  const [archiveRemarks, setArchiveRemarks] = useState("");
  
  // Employee Form State
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeForm, setEmployeeForm] = useState({
    employee_code: "",
    name: "",
    phone: "",
    email: "",
    address: "",
    id_card_return_address: "",
    blood_group: "",
    designation: "",
    department: "Site",
    category: "Skilled",
    employment_type: "Permanent",
    date_of_joining: new Date().toISOString().split('T')[0],
    reporting_to: "",
    aadhar_number: "",
    pan_number: "",
    bank_account: "",
    bank_name: "",
    ifsc_code: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    basic_salary: "",
    daily_wage_rate: "",
    overtime_rate: "",
    assigned_project_id: ""
  });

  // Leave Form State
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    employee_id: "",
    leave_type: "Casual",
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    days_count: 1,
    reason: ""
  });

  // Document State
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [selectedEmployeeForDocs, setSelectedEmployeeForDocs] = useState(null);
  const [employeeDocuments, setEmployeeDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docForm, setDocForm] = useState({
    document_type: "",
    document_name: "",
    file: null
  });

  // Photo Upload State
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [selectedEmployeeForPhoto, setSelectedEmployeeForPhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);

  // Leave Approval State
  const [leaveApprovalDialogOpen, setLeaveApprovalDialogOpen] = useState(false);
  const [leaveToApprove, setLeaveToApprove] = useState(null);
  const [revisedDaysCount, setRevisedDaysCount] = useState(0);
  const [approvalRemarks, setApprovalRemarks] = useState("");

  const currentUser = getUser() || {};
  // Only Management, Master Admin, and Project Director can approve/reject leaves
  const canApproveLeaves = currentUser.email === "shiva.mittal@kpminfra.com" || 
    ["Master Admin", "Management", "Project Director"].includes(currentUser.role);
  
  // Projects state for navigation and employee assignment
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    fetchData();
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await apiClient.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error("Failed to fetch projects");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [empRes, leaveRes, statsRes] = await Promise.all([
        apiClient.get('/employees'),
        apiClient.get('/leaves'),
        apiClient.get('/employees/stats/summary')
      ]);
      setEmployees(empRes.data);
      setLeaves(leaveRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to fetch HR data");
    } finally {
      setLoading(false);
    }
  };

  // Employee Functions
  const handleEmployeeSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...employeeForm,
        basic_salary: parseFloat(employeeForm.basic_salary) || 0,
        daily_wage_rate: parseFloat(employeeForm.daily_wage_rate) || 0,
        overtime_rate: parseFloat(employeeForm.overtime_rate) || 0
      };

      if (editingEmployee) {
        await apiClient.put(`/employees/${editingEmployee.id}`, data);
        toast.success("Employee updated successfully");
      } else {
        await apiClient.post('/employees', data);
        toast.success("Employee added successfully");
      }
      
      setEmployeeDialogOpen(false);
      setEditingEmployee(null);
      resetEmployeeForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save employee");
    }
  };

  const handleEditEmployee = (emp) => {
    setEditingEmployee(emp);
    setEmployeeForm({
      employee_code: emp.employee_code,
      name: emp.name,
      phone: emp.phone,
      email: emp.email || "",
      address: emp.address || "",
      id_card_return_address: emp.id_card_return_address || "",
      blood_group: emp.blood_group || "",
      designation: emp.designation,
      department: emp.department,
      category: emp.category,
      employment_type: emp.employment_type,
      date_of_joining: emp.date_of_joining,
      reporting_to: emp.reporting_to || "",
      aadhar_number: emp.aadhar_number || "",
      pan_number: emp.pan_number || "",
      bank_account: emp.bank_account || "",
      bank_name: emp.bank_name || "",
      ifsc_code: emp.ifsc_code || "",
      emergency_contact_name: emp.emergency_contact_name || "",
      emergency_contact_phone: emp.emergency_contact_phone || "",
      basic_salary: emp.basic_salary?.toString() || "",
      daily_wage_rate: emp.daily_wage_rate?.toString() || "",
      overtime_rate: emp.overtime_rate?.toString() || "",
      assigned_project_id: emp.assigned_project_id || ""
    });
    setEmployeeDialogOpen(true);
  };

  const handleDeleteEmployee = async (empId, empName) => {
    if (!window.confirm(`Are you sure you want to deactivate "${empName}"?`)) return;
    
    try {
      await apiClient.delete(`/employees/${empId}`);
      toast.success("Employee deactivated");
      fetchData();
    } catch (error) {
      toast.error("Failed to deactivate employee");
    }
  };

  // Archive employee (staff removal)
  const openArchiveDialog = (emp) => {
    setEmployeeToArchive(emp);
    setArchiveReason("");
    setArchiveRemarks("");
    setArchiveDialogOpen(true);
  };
  
  const handleArchiveEmployee = async () => {
    if (!employeeToArchive || !archiveReason.trim()) {
      toast.error("Please provide a reason for archiving");
      return;
    }
    
    try {
      await apiClient.post(`/employees/${employeeToArchive.id}/archive`, {
        reason: archiveReason,
        remarks: archiveRemarks
      });
      toast.success("Employee archived (removed) successfully");
      setArchiveDialogOpen(false);
      setEmployeeToArchive(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive employee");
    }
  };

  const resetEmployeeForm = () => {
    setEmployeeForm({
      employee_code: "",
      name: "",
      phone: "",
      email: "",
      address: "",
      id_card_return_address: "",
      blood_group: "",
      designation: "",
      department: "Site",
      category: "Skilled",
      employment_type: "Permanent",
      date_of_joining: new Date().toISOString().split('T')[0],
      reporting_to: "",
      aadhar_number: "",
      pan_number: "",
      bank_account: "",
      bank_name: "",
      ifsc_code: "",
      emergency_contact_name: "",
      emergency_contact_phone: "",
      basic_salary: "",
      daily_wage_rate: "",
      overtime_rate: "",
      assigned_project_id: ""
    });
  };

  // Leave Functions
  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/leaves', leaveForm);
      toast.success("Leave request submitted");
      setLeaveDialogOpen(false);
      setLeaveForm({
        employee_id: "",
        leave_type: "Casual",
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        days_count: 1,
        reason: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit leave request");
    }
  };

  // Open leave approval dialog
  const openLeaveApprovalDialog = (leave) => {
    setLeaveToApprove(leave);
    setRevisedDaysCount(leave.days_count);
    setApprovalRemarks("");
    setLeaveApprovalDialogOpen(true);
  };

  const handleApproveLeave = async () => {
    if (!leaveToApprove) return;
    
    try {
      await apiClient.put(`/leaves/${leaveToApprove.id}/approve`, {
        revised_days_count: revisedDaysCount,
        remarks: approvalRemarks
      });
      toast.success("Leave approved successfully");
      setLeaveApprovalDialogOpen(false);
      setLeaveToApprove(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve leave");
    }
  };

  const handleRejectLeave = async (leaveId) => {
    const reason = prompt("Enter rejection reason:");
    if (reason === null) return;
    
    try {
      await apiClient.put(`/leaves/${leaveId}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success("Leave rejected");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject leave");
    }
  };

  // Document Functions
  const openDocumentDialog = async (employee) => {
    setSelectedEmployeeForDocs(employee);
    setDocumentDialogOpen(true);
    await fetchEmployeeDocuments(employee.id);
    await fetchDocumentTypes();
  };

  const fetchEmployeeDocuments = async (employeeId) => {
    try {
      const response = await apiClient.get(`/employees/${employeeId}/documents`);
      setEmployeeDocuments(response.data);
    } catch (error) {
      console.error("Failed to fetch documents");
    }
  };

  const fetchDocumentTypes = async () => {
    try {
      const response = await apiClient.get('/document-types/employee');
      setDocumentTypes(response.data);
    } catch (error) {
      setDocumentTypes([
        "ID Proof", "Address Proof", "Educational Certificate", "Experience Certificate",
        "PAN Card", "Aadhar Card", "Bank Details", "Appointment Letter", "Contract",
        "Medical Certificate", "Photo", "Other"
      ]);
    }
  };

  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    if (!docForm.file || !docForm.document_type) {
      toast.error("Please select a file and document type");
      return;
    }

    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', docForm.file);
      formData.append('document_type', docForm.document_type);
      formData.append('document_name', docForm.document_name || docForm.file.name);

      await apiClient.post(`/employees/${selectedEmployeeForDocs.id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success("Document uploaded successfully");
      setDocForm({ document_type: "", document_name: "", file: null });
      await fetchEmployeeDocuments(selectedEmployeeForDocs.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDocumentDownload = async (doc) => {
    try {
      const token = getToken();
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/employees/${selectedEmployeeForDocs.id}/documents/${doc.id}/download`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = doc.document_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success("Document downloaded");
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const handleDocumentDelete = async (doc) => {
    if (!window.confirm(`Delete document "${doc.document_name}"?`)) return;
    
    try {
      await apiClient.delete(`/employees/${selectedEmployeeForDocs.id}/documents/${doc.id}`);
      toast.success("Document deleted");
      await fetchEmployeeDocuments(selectedEmployeeForDocs.id);
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  // Photo Upload Functions
  const openPhotoDialog = (employee) => {
    setSelectedEmployeeForPhoto(employee);
    setPhotoFile(null);
    setPhotoDialogOpen(true);
  };

  const handlePhotoUpload = async (e) => {
    e.preventDefault();
    if (!photoFile) {
      toast.error("Please select a photo");
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', photoFile);

      await apiClient.post(`/employees/${selectedEmployeeForPhoto.id}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success("Photo uploaded successfully");
      setPhotoDialogOpen(false);
      setPhotoFile(null);
      fetchData(); // Refresh to get updated photo_url
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDownloadIdCard = async (employee) => {
    try {
      const token = getToken();
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/employees/${employee.id}/id-card`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `ID_Card_${employee.employee_code}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success("ID Card downloaded");
    } catch (error) {
      toast.error("Failed to download ID Card. Please ensure photo is uploaded.");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Calculate days between dates
  const calculateDays = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? diff : 1;
  };

  // Filter employees
  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         emp.employee_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         emp.designation.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || emp.category === categoryFilter;
    const matchesStatus = !statusFilter || emp.status === statusFilter;
    const matchesProject = !projectFilter || 
                          (projectFilter === "unassigned" ? !emp.assigned_project_id : emp.assigned_project_id === projectFilter);
    return matchesSearch && matchesCategory && matchesStatus && matchesProject;
  });

  // Get project name for display
  const getProjectName = (projectId) => {
    if (!projectId) return "Unassigned";
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : "Unknown";
  };

  const getStatusBadge = (status) => {
    const config = {
      "Pending": { color: "bg-warning/20 text-warning", icon: Clock },
      "Approved": { color: "bg-success/20 text-success", icon: CheckCircle },
      "Rejected": { color: "bg-error/20 text-error", icon: XCircle },
      "Cancelled": { color: "bg-slate-200 text-slate-600", icon: XCircle }
    };
    const { color, icon: Icon } = config[status] || config["Pending"];
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-sm text-xs font-medium ${color}`}>
        <Icon className="w-3 h-3" />
        <span>{status}</span>
      </span>
    );
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  const ctx = {
    approvalRemarks,
    archiveDialogOpen,
    archiveReason,
    archiveRemarks,
    calculateDays,
    canApproveLeaves,
    categoryFilter,
    docForm,
    documentDialogOpen,
    documentTypes,
    editingEmployee,
    employeeDialogOpen,
    employeeDocuments,
    employeeForm,
    employeeToArchive,
    employees,
    filteredEmployees,
    formatFileSize,
    getProjectName,
    getStatusBadge,
    handleApproveLeave,
    handleArchiveEmployee,
    handleDeleteEmployee,
    handleDocumentDelete,
    handleDocumentDownload,
    handleDocumentUpload,
    handleDownloadIdCard,
    handleEditEmployee,
    handleEmployeeSubmit,
    handleLeaveSubmit,
    handlePhotoUpload,
    handleRejectLeave,
    leaveApprovalDialogOpen,
    leaveDialogOpen,
    leaveForm,
    leaveToApprove,
    leaves,
    openArchiveDialog,
    openDocumentDialog,
    openLeaveApprovalDialog,
    openPhotoDialog,
    photoDialogOpen,
    photoFile,
    projectFilter,
    projects,
    resetEmployeeForm,
    revisedDaysCount,
    searchTerm,
    selectedEmployeeForDocs,
    selectedEmployeeForPhoto,
    setApprovalRemarks,
    setArchiveDialogOpen,
    setArchiveReason,
    setArchiveRemarks,
    setCategoryFilter,
    setDocForm,
    setDocumentDialogOpen,
    setEditingEmployee,
    setEmployeeDialogOpen,
    setEmployeeForm,
    setLeaveApprovalDialogOpen,
    setLeaveDialogOpen,
    setLeaveForm,
    setPhotoDialogOpen,
    setPhotoFile,
    setProjectFilter,
    setRevisedDaysCount,
    setSearchTerm,
    setStatusFilter,
    statusFilter,
    uploadingDoc,
    uploadingPhoto
  };

  return (
    <div>
      {/* Back Navigation */}
      <div className="mb-6">
        <Link 
          to="/projects" 
          className="inline-flex items-center text-sm text-slate-600 hover:text-primary transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Projects
        </Link>
      </div>

      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">HR MANAGEMENT</h1>
          <p className="text-slate-600 mt-2 font-body">Employee Database & Leave Management</p>
        </div>
      </div>

      {/* Quick Project Access Cards */}
      {projects.length > 0 && (
        <div className="mb-8">
          <h3 className="font-heading font-bold text-sm uppercase tracking-widest text-slate-700 mb-4">
            <FolderOpen className="w-4 h-4 inline mr-2" />
            Quick Access - Project Workforce
          </h3>
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
            {projects.slice(0, 4).map((project) => (
              <Link 
                key={project.id}
                to={`/workforce/${project.id}`}
                className="stat-card hover:shadow-md hover:border-primary/30 transition-all group"
              >
                <p className="font-mono text-xs text-slate-500 mb-1">{project.project_code}</p>
                <p className="font-heading font-bold text-primary group-hover:text-accent transition-colors line-clamp-1">
                  {project.name}
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  Click to view Attendance & Staff
                </p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Employees</p>
          <p className="font-heading font-black text-4xl text-primary font-mono" data-testid="total-employees">
            {stats?.total_employees || 0}
          </p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Active Employees</p>
          <p className="font-heading font-black text-4xl text-success font-mono" data-testid="active-employees">
            {stats?.active_employees || 0}
          </p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Pending Leaves</p>
          <p className="font-heading font-black text-4xl text-warning font-mono" data-testid="pending-leaves">
            {leaves.filter(l => l.status === "Pending").length}
          </p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Categories</p>
          <div className="text-sm space-y-1">
            {stats?.by_category && Object.entries(stats.by_category).map(([cat, count]) => (
              <div key={cat} className="flex justify-between">
                <span className="text-slate-600">{cat}</span>
                <span className="font-mono font-semibold">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b border-slate-200">
        <button
          onClick={() => setActiveTab("employees")}
          data-testid="tab-employees"
          className={`pb-3 px-4 font-medium text-sm transition-colors ${
            activeTab === "employees" 
              ? "border-b-2 border-primary text-primary" 
              : "text-slate-600 hover:text-primary"
          }`}
        >
          <Users className="w-4 h-4 inline mr-2" />
          Employees ({employees.length})
        </button>
        <button
          onClick={() => setActiveTab("leaves")}
          data-testid="tab-leaves"
          className={`pb-3 px-4 font-medium text-sm transition-colors ${
            activeTab === "leaves" 
              ? "border-b-2 border-primary text-primary" 
              : "text-slate-600 hover:text-primary"
          }`}
        >
          <Calendar className="w-4 h-4 inline mr-2" />
          Leave Requests ({leaves.length})
        </button>
      </div>

      {/* Employees Tab */}
      {activeTab === "employees" && <EmployeesSection ctx={ctx} />}

      {/* Leaves Tab */}
      {activeTab === "leaves" && <LeavesSection ctx={ctx} />}

      {/* Employee Documents Dialog */}
      <DocumentDialogSection ctx={ctx} />

      {/* Photo Upload Dialog */}
      <PhotoDialogSection ctx={ctx} />

      {/* Archive Employee Dialog */}
      <ArchiveDialogSection ctx={ctx} />

      {/* Leave Approval Dialog */}
      <LeaveApprovalDialogSection ctx={ctx} />
    </div>
  );
};

export default HRManagement;
